import { X, Printer, Send, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useState } from "react";
import Portal from "@/components/Portal";
import { BrandLogo } from "@/components/BrandLogo";

// Free-text sections shown in order. Every label is bilingual English · മലയാളം.
const TEXT_SECTIONS = [
  { key: "reason_for_admission", label: "Reason for admission · പ്രവേശനത്തിനുള്ള കാരണം", rows: 2 },
  { key: "why_admitted", label: "Why admitted for this treatment · ഈ ചികിത്സയ്ക്കായി പ്രവേശിപ്പിച്ചത് എന്തുകൊണ്ട്", rows: 2 },
  { key: "significant_findings", label: "Significant findings · പ്രധാന കണ്ടെത്തലുകൾ", rows: 3 },
  { key: "medicines_before_admission", label: "Medicines given before admission · പ്രവേശനത്തിന് മുമ്പ് നൽകിയ മരുന്നുകൾ", rows: 3 },
  { key: "diagnosis", label: "Diagnosis · രോഗനിർണ്ണയം", rows: 2 },
  { key: "treatment_given", label: "Treatment given · ചികിത്സ", rows: 3 },
  { key: "condition_at_discharge", label: "Condition at discharge · ഡിസ്ചാർജ് സമയത്തെ സ്ഥിതി", rows: 2 },
  { key: "medications_at_discharge", label: "Discharge medications · വീട്ടിലേക്കുള്ള മരുന്നുകൾ", rows: 3 },
  { key: "advice", label: "Advice · നിർദ്ദേശങ്ങൾ", rows: 3 },
  { key: "follow_up", label: "Follow-up · തുടർപരിശോധന", rows: 2 },
];

// Yes / No sections — when Yes, a textarea appears for details.
const YES_NO_SECTIONS = [
  {
    boolKey: "investigations_done",
    textKey: "investigation_details",
    label: "Investigation results · പരിശോധനാ ഫലങ്ങൾ",
    placeholder: "Blood reports, imaging findings, lab values · രക്തപരിശോധന, സ്കാൻ, ലാബ് വിവരങ്ങൾ",
    rows: 4,
  },
  {
    boolKey: "procedure_done",
    textKey: "procedure_details",
    label: "Procedure details / performed · ചെയ്ത നടപടികളുടെ വിവരം",
    placeholder: "Procedure(s) performed with dates · ചെയ്ത നടപടികളും തീയതികളും",
    rows: 3,
  },
  {
    boolKey: "other_treatment_done",
    textKey: "other_treatment_details",
    label: "Other treatments given · മറ്റ് ചികിത്സകൾ",
    placeholder: "Any other treatments alongside the main plan · പ്രധാന ചികിത്സയോടൊപ്പം ചെയ്തവ",
    rows: 3,
  },
];

const emptyNotes = (src = {}) => ({
  reason_for_admission: src.reason_for_admission || "",
  why_admitted: src.why_admitted || "",
  significant_findings: src.significant_findings || "",
  medicines_before_admission: src.medicines_before_admission || "",
  investigations_done: src.investigations_done ?? null,
  investigation_details: src.investigation_details || "",
  procedure_done: src.procedure_done ?? null,
  procedure_details: src.procedure_details || "",
  other_treatment_done: src.other_treatment_done ?? null,
  other_treatment_details: src.other_treatment_details || "",
  diagnosis: src.diagnosis || "",
  treatment_given: src.treatment_given || "",
  condition_at_discharge: src.condition_at_discharge || "",
  medications_at_discharge: src.medications_at_discharge || "",
  advice: src.advice || "",
  follow_up: src.follow_up || "",
  digitally_signed: !!src.digitally_signed,
});

export default function DischargeSummary({ admission: initial, branchName, onClose }) {
  const [admission, setAdmission] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!initial?.summary_notes || Object.keys(initial.summary_notes || {}).length === 0);
  const [notes, setNotes] = useState(() => emptyNotes(initial?.summary_notes || {}));

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admissions/${admission.id}/discharge-notes`, notes);
      setAdmission(data);
      setNotes(emptyNotes(data.summary_notes || {}));
      setEditing(false);
      toast.success("Discharge summary saved");
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const send = async (channel) => {
    setBusy(true);
    try {
      await api.post(`/admissions/${admission.id}/send-followup`, null, { params: { channel } });
      toast.success(channel === "both" ? "Sent on SMS + WhatsApp" : channel === "whatsapp" ? "WhatsApp follow-up sent" : "SMS sent");
    } catch (err) {
      toast.error(formatApiError(err, "Send failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 print:static print:bg-white print:p-0" onClick={onClose}>
        <div
          className="bg-white w-full max-w-2xl max-h-[92vh] flex flex-col rounded-lg border border-sand print:max-h-none print:rounded-none print:border-none print:shadow-none print:block"
          onClick={(e) => e.stopPropagation()}
          data-testid="discharge-summary-modal"
        >
          {/* Toolbar (hidden on print) */}
          <div className="px-6 py-3 border-b border-sand flex flex-wrap items-center justify-between gap-2 print:hidden bg-white shrink-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652]">
              Discharge summary · ഡിസ്ചാർജ് സംഗ്രഹം · {admission.admission_no}
            </div>
            <div className="flex gap-2 flex-wrap">
              {editing ? (
                <button onClick={saveNotes} disabled={saving} data-testid="ds-save-notes"
                  className="h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2 disabled:opacity-60">
                  <Save className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {saving ? "Saving…" : "Save summary"}
                </button>
              ) : (
                <button onClick={() => setEditing(true)} data-testid="ds-edit-notes"
                  className="h-9 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
                  Edit
                </button>
              )}
              <button onClick={() => send("whatsapp")} disabled={busy} data-testid="ds-send-whatsapp"
                className="h-9 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2 disabled:opacity-60">
                <Send className="h-3.5 w-3.5" strokeWidth={1.75} /> WhatsApp
              </button>
              <button onClick={() => send("sms")} disabled={busy} data-testid="ds-send-sms"
                className="h-9 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2 disabled:opacity-60">
                <Send className="h-3.5 w-3.5" strokeWidth={1.75} /> SMS
              </button>
              <button onClick={() => window.print()} data-testid="ds-print"
                className="h-9 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
                <Printer className="h-3.5 w-3.5" strokeWidth={1.75} /> Print
              </button>
              <button onClick={onClose} className="h-9 w-9 rounded-lg border border-sand hover:bg-[#F9F6F0] flex items-center justify-center" data-testid="ds-close">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Printable content */}
          <div className="p-8 print:p-6 discharge-print overflow-y-auto print:overflow-visible">
            <div className="border-b-2 border-forest pb-4 mb-6">
              <div className="flex items-center justify-center">
                <BrandLogo size="h-14" />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-[#5C5652]">
                <span className="uppercase tracking-wider">Discharge Summary · ഡിസ്ചാർജ് സംഗ്രഹം · Branch: {branchName || admission.branch_code}</span>
                <span>Admission {admission.admission_no}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <Row label="Patient · രോഗി" value={admission.patient_name} />
              <Row label="MRN" value={admission.mrn} />
              <Row label="Phone · ഫോൺ" value={admission.patient_phone} />
              <Row label="Room · മുറി" value={`${admission.room_number} · ${admission.room_type}`} />
              <Row label="Admitted · പ്രവേശിച്ചത്" value={new Date(admission.admitted_at).toLocaleString()} />
              <Row label="Discharged · ഡിസ്ചാർജ് ചെയ്തത്" value={admission.discharged_at ? new Date(admission.discharged_at).toLocaleString() : "—"} />
              <Row label="Days · ദിവസങ്ങൾ" value={admission.days} />
              <Row label="Reason · കാരണം" value={admission.reason || "—"} />
            </div>

            {/* Clinical notes (editable) */}
            <div className="mb-6 space-y-5" data-testid="ds-notes">
              {TEXT_SECTIONS.map((f) => (
                <div key={f.key}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652] mb-1">{f.label}</div>
                  {editing ? (
                    <textarea
                      data-testid={`ds-${f.key}`}
                      value={notes[f.key]}
                      onChange={(e) => setNotes({ ...notes, [f.key]: e.target.value })}
                      rows={f.rows}
                      className="w-full px-3 py-2 rounded border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 resize-none"
                    />
                  ) : (
                    <div className="text-sm whitespace-pre-wrap min-h-[1.2rem]" data-testid={`ds-${f.key}-view`}>
                      {notes[f.key] || <span className="text-[#5C5652] italic">—</span>}
                    </div>
                  )}
                </div>
              ))}

              {YES_NO_SECTIONS.map((f) => (
                <YesNoSection
                  key={f.boolKey}
                  editing={editing}
                  label={f.label}
                  placeholder={f.placeholder}
                  rows={f.rows}
                  yes={notes[f.boolKey]}
                  detail={notes[f.textKey]}
                  onYes={(v) => setNotes({ ...notes, [f.boolKey]: v })}
                  onDetail={(v) => setNotes({ ...notes, [f.textKey]: v })}
                  boolKey={f.boolKey}
                  textKey={f.textKey}
                />
              ))}
            </div>

            <div className="mb-4" data-testid="ds-signature-block">
              <label className="flex items-center gap-3 p-3 rounded-lg bg-[#F9F6F0] border border-sand cursor-pointer print:border-forest">
                <input
                  type="checkbox"
                  data-testid="ds-digital-sign"
                  checked={!!notes.digitally_signed}
                  disabled={!editing}
                  onChange={(e) => setNotes({ ...notes, digitally_signed: e.target.checked })}
                  className="h-4 w-4 accent-[#1A4331]"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-forest">Digitally signed &amp; verified · ഡിജിറ്റലായി സാക്ഷ്യപ്പെടുത്തിയത്</div>
                  <div className="text-[11px] text-[#5C5652]">
                    {notes.digitally_signed
                      ? `Verified by ${admission.summary_notes?.updated_by || "—"} on ${admission.summary_notes?.updated_at ? new Date(admission.summary_notes.updated_at).toLocaleString() : "save pending"} — physical signature not required.`
                      : "Tick to certify this summary is complete. A physical signature will not be required once saved."}
                  </div>
                </div>
                {notes.digitally_signed && (
                  <span className="text-forest font-bold text-xs uppercase tracking-wider">✓ Verified</span>
                )}
              </label>
            </div>

            <div className="mb-6">
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">Bill breakdown · ബിൽ വിശദാംശം</div>
              <table className="w-full text-sm border border-sand rounded overflow-hidden">
                <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Item</th>
                    <th className="text-right px-3 py-2 font-bold">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E3D9]">
                  <tr>
                    <td className="px-3 py-2">Room charges — {admission.days} day(s) × ₹ {Number(admission.tariff_per_day).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(admission.room_charges_total).toFixed(2)}</td>
                  </tr>
                  {(admission.charges || []).map((c, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">{c.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(c.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-sand bg-[#F9F6F0]">
                    <td className="px-3 py-2 font-semibold">Grand total</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-forest">{Number(admission.grand_total).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-[#5C5652]">Deposit paid</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(admission.deposit).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-semibold">{admission.balance_due >= 0 ? "Balance due" : "Refund"}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${admission.balance_due >= 0 ? "text-[#C25946]" : "text-forest"}`}>
                      {Math.abs(Number(admission.balance_due)).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="text-xs text-[#5C5652] mt-6 pt-6 border-t border-sand">
              Please rest and follow the doctor&apos;s advice. Bring this summary for your next visit. · വിശ്രമിക്കുകയും ഡോക്ടറുടെ നിർദ്ദേശം പാലിക്കുകയും ചെയ്യുക. അടുത്ത സന്ദർശനത്തിനായി ഈ സംഗ്രഹം കൊണ്ടുവരിക.
            </div>
          </div>
        </div>

        <style>{`
          @media print {
            @page { margin: 12mm; }
            body { background: white !important; }
            .print\\:hidden { display: none !important; }
          }
        `}</style>
      </div>
    </Portal>
  );
}

function YesNoSection({ editing, label, placeholder, rows, yes, detail, onYes, onDetail, boolKey, textKey }) {
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652]">{label}</div>
        {editing ? (
          <div className="inline-flex rounded-md border border-sand overflow-hidden" role="radiogroup" aria-label={label}>
            <button type="button" onClick={() => onYes(true)} data-testid={`ds-${boolKey}-yes`}
              className={`px-3 h-7 text-xs font-semibold ${yes === true ? "bg-forest text-white" : "bg-white text-[#5C5652] hover:bg-[#F9F6F0]"}`}>
              Yes · അതെ
            </button>
            <button type="button" onClick={() => onYes(false)} data-testid={`ds-${boolKey}-no`}
              className={`px-3 h-7 text-xs font-semibold border-l border-sand ${yes === false ? "bg-[#C25946] text-white" : "bg-white text-[#5C5652] hover:bg-[#F9F6F0]"}`}>
              No · ഇല്ല
            </button>
          </div>
        ) : (
          <span className={`text-[11px] font-bold uppercase tracking-wider ${yes === true ? "text-forest" : yes === false ? "text-[#C25946]" : "text-[#5C5652]"}`}>
            {yes === true ? "Yes · അതെ" : yes === false ? "No · ഇല്ല" : "—"}
          </span>
        )}
      </div>
      {yes === true && (
        editing ? (
          <textarea
            data-testid={`ds-${textKey}`}
            value={detail}
            onChange={(e) => onDetail(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className="w-full px-3 py-2 rounded border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 resize-none"
          />
        ) : (
          <div className="text-sm whitespace-pre-wrap min-h-[1.2rem]" data-testid={`ds-${textKey}-view`}>
            {detail || <span className="text-[#5C5652] italic">—</span>}
          </div>
        )
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652]">{label}</div>
      <div className="text-[#2A2624]">{value}</div>
    </div>
  );
}
